/**
 * useAccessCheck - Hook to check user's access to specific apps/features
 * Returns access status and upgrade prompt trigger
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/global/auth/AuthContext";
import { supabase } from "@/lib/supabase";

interface AccessCheckResult {
  hasAccess: boolean;
  isLoading: boolean;
  currentPlan: string | null;
  requiredPlan: "fma_pro" | "fma_elite" | "ams_standard" | null;
  showUpgradePrompt: boolean;
  openUpgradePrompt: () => void;
  closeUpgradePrompt: () => void;
}

interface AccessCheckOptions {
  app: "FMA" | "AMS";
  feature?: string;
  autoCheck?: boolean; // If true, automatically checks on mount
}

export function useAccessCheck(options: AccessCheckOptions): AccessCheckResult {
  const { activeTenantId, user } = useAuth();
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [requiredPlan, setRequiredPlan] = useState<"fma_pro" | "fma_elite" | "ams_standard" | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState<boolean>(false);

  const checkAccess = async () => {
    if (!activeTenantId || !user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // Query the effective access view
      const { data, error } = await supabase
        .from("v_org_effective_access")
        .select("*")
        .eq("organization_id", activeTenantId)
        .single();

      if (error) {
        console.error("[useAccessCheck] Error fetching access:", error);
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      // Determine current plan from fma_plan (returned by view)
      const planCode = data.fma_plan || "fma_basic";
      setCurrentPlan(planCode);

      // Check access based on app
      if (options.app === "AMS") {
        const amsAccess = data.has_ams_access && data.has_active_billing;
        setHasAccess(amsAccess);

        if (!amsAccess) {
          // Determine what plan they need
          // If they have no AMS access at all, recommend AMS Standard
          // Unless they might benefit from Elite (which includes FMA Pro + AMS)
          if (planCode === "fma_basic" || planCode === "fma_pro") {
            setRequiredPlan("fma_elite"); // Recommend Elite for better value
          } else {
            setRequiredPlan("ams_standard");
          }
        }
      } else if (options.app === "FMA") {
        // FMA has universal basic access, but check for premium features
        const fmaBasicAccess = data.has_fma_access;
        setHasAccess(fmaBasicAccess);

        // If checking for premium FMA features, might need upgrade
        if (options.feature === "pro" && planCode === "fma_basic") {
          setHasAccess(false);
          setRequiredPlan("fma_pro");
        } else if (options.feature === "elite" && planCode !== "fma_elite") {
          setHasAccess(false);
          setRequiredPlan("fma_elite");
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error("[useAccessCheck] Exception:", err);
      setHasAccess(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (options.autoCheck !== false) {
      checkAccess();
    }
  }, [activeTenantId, user, options.app, options.feature]);

  const openUpgradePrompt = () => {
    setShowUpgradePrompt(true);
  };

  const closeUpgradePrompt = () => {
    setShowUpgradePrompt(false);
  };

  return {
    hasAccess,
    isLoading,
    currentPlan,
    requiredPlan,
    showUpgradePrompt,
    openUpgradePrompt,
    closeUpgradePrompt,
  };
}
